import React from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import { Alert, Box, Collapse, Stack, TableCell, TableRow } from '@mui/material';

import type { Dayjs } from '~/services/TimeService';

import { dropdownContentSx, handleExpandRowKeyDown } from '../surcharges/surchargeTabLayout';

import BlackoutMatchStatusChip from './BlackoutMatchStatusChip';
import MatchedBlackoutRoomRateCards from './MatchedBlackoutRoomRateCards';
import MissingBlackoutRoomTypeCards from './MissingBlackoutRoomTypeCards';
import ScopeCell from './ScopeCell';
import {
  type MissingBlackoutRoomType,
  type RoomRateBlackoutAssignment,
  formatBlackoutRange,
} from './appliedBlackoutUtils';
import {
  type DaysScopeResult,
  type RoomsScopeResult,
  SCOPE_SOURCE_LABEL,
  formatDaysScope,
  formatRoomsScope,
  getDaysScopeChipColor,
  getRoomsScopeChipColor,
} from './blackoutScopeClassifier';
import type { SalesforceBlackoutDate } from './types';

const nameCellSx = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
} as const;

interface Props {
  blackoutDate: SalesforceBlackoutDate;
  blackoutDatesByRoomRateError?: unknown;
  columnCount: number;
  days: DaysScopeResult;
  isExpanded: boolean;
  matchingAssignments: Array<RoomRateBlackoutAssignment>;
  missingRoomTypes: Array<MissingBlackoutRoomType>;
  now: Dayjs;
  onToggleExpanded: () => void;
  rooms: RoomsScopeResult;
}

export default function SalesforceBlackoutRow({
  blackoutDate,
  blackoutDatesByRoomRateError,
  columnCount,
  days,
  isExpanded,
  matchingAssignments,
  missingRoomTypes,
  now,
  onToggleExpanded,
  rooms,
}: Props) {
  return (
    <>
      <TableRow
        hover
        role="button"
        sx={{ cursor: 'pointer' }}
        tabIndex={0}
        onClick={onToggleExpanded}
        onKeyDown={(event) => handleExpandRowKeyDown(event, onToggleExpanded)}
      >
        <TableCell>{isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}</TableCell>
        <TableCell sx={nameCellSx} title={blackoutDate.name}>
          <Stack alignItems="center" direction="row" spacing={1} sx={{ minWidth: 0 }}>
            <Box component="span" sx={nameCellSx}>
              {blackoutDate.name}
            </Box>
            <BlackoutMatchStatusChip
              blackoutDate={blackoutDate}
              hasMatchingAssignments={matchingAssignments.length > 0}
            />
          </Stack>
        </TableCell>
        <TableCell>
          <ScopeCell
            color={getRoomsScopeChipColor(rooms)}
            tooltip={SCOPE_SOURCE_LABEL[rooms.source]}
            value={formatRoomsScope(rooms)}
          />
        </TableCell>
        <TableCell sx={{ whiteSpace: 'nowrap' }}>
          {formatBlackoutRange(blackoutDate.from__c, blackoutDate.to__c)}
        </TableCell>
        <TableCell>
          <ScopeCell
            color={getDaysScopeChipColor(days)}
            tooltip={SCOPE_SOURCE_LABEL[days.source]}
            value={formatDaysScope(days)}
          />
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={columnCount} sx={{ p: 0 }}>
          <Collapse unmountOnExit in={isExpanded} sx={{ width: '100%' }} timeout="auto">
            <Box sx={dropdownContentSx}>
              {blackoutDatesByRoomRateError ? (
                <Alert severity="error">Unable to load room-rate blackout dates.</Alert>
              ) : (
                <Stack spacing={1}>
                  <MatchedBlackoutRoomRateCards
                    assignments={matchingAssignments}
                    hideEmptyState={missingRoomTypes.length > 0}
                    now={now}
                  />
                  <MissingBlackoutRoomTypeCards missingRoomTypes={missingRoomTypes} />
                </Stack>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}
